import React, { type ReactElement, useEffect, useState } from 'react'
import { ReactComponent as AuctionIcon } from 'assets/auction.svg'
import parse from 'html-react-parser'
import theme from 'common/utils/theme'
import { CircularProgress, Divider, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useAppSelector } from 'store/hooks'
import backgroundGraphics from 'assets/tenderBackgroud.svg'
import { getTopicIcon, getTopicLabel, getTopicColor } from 'common/tenders/tenders.functions'
import { formatDate } from 'utils/functions'
import { downloadHTML } from 'common/files/files.service'
import { type Topic } from 'features/bids/bid.types'
import VendorInfoPanel from 'features/vendor/vendor-profile/components/vendor-info-panel'
import SimpleTooltip from 'common/ui/simple-tooltip'

import styles from './TenderDetails.module.scss'

const getServices = (topic: Topic): JSX.Element | null => {
  const color: string = getTopicColor(topic.type)
  const categoryName = topic.category?.name
  return (
    <div key={topic.topicId} className={styles.serviceLabel}>
      <div className={styles.serviceNameLabel}>
        <Typography variant="title1" className={styles.labelText} sx={{ color, borderColor: color }}>
          {getTopicIcon(topic.type)}
          {getTopicLabel(topic.type)}
        </Typography>
        <SimpleTooltip title={categoryName ?? '-'} className={styles.tooltip}>
          {categoryName ? (
            <>
              <Typography variant="subtitle1">(</Typography>
              <Typography className={styles.truncate} variant="subtitle1">
                {categoryName}
              </Typography>
              <Typography variant="subtitle1">)</Typography>
            </>
          ) : (
            <></>
          )}
        </SimpleTooltip>
      </div>
      <Typography className={styles.topic} variant="title2" sx={{ color: theme.palette.white.main }}>
        {topic.name ?? '-'}
      </Typography>
    </div>
  )
}

const TenderDetails: React.FC = (): ReactElement => {
  const { proposal } = useAppSelector((state) => state.bid)
  const { palette } = useTheme()
  const [content, setContent] = useState<string>()

  const handleRTEDownload = async (): Promise<void> => {
    if (proposal?.tender?.description?.read?.url) {
      try {
        const content = await downloadHTML(proposal?.tender.description.read.url)
        setContent(content)
      } catch (e) {
        console.error(e)
      }
    }
  }

  useEffect(() => {
    if (proposal?.tender?.description) {
      void handleRTEDownload()
    }
  }, [proposal?.tender?.description])

  return proposal?.tender ? (
    <div className={styles.container}>
      <VendorInfoPanel
        title={proposal?.tender.name}
        icon={<AuctionIcon fill={theme.palette.green.main} />}
        className={styles.referentDocumentsPanel}
        contentClassName={styles.documents}
        rightHeader={
          <>
            <>
              <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                Rok za davanje ponude{' '}
              </Typography>
              <Typography variant="subtitle1">
                {formatDate(new Date(proposal.tender.proposalDeadline))}
              </Typography>
            </>
            <>
              <Typography variant="subtitle1" sx={{ color: palette.gray.main }}>
                Kraj tendera{' '}
              </Typography>
              <Typography variant="subtitle1">
                {formatDate(new Date(proposal.tender.resolutionDeadline))}
              </Typography>
            </>
          </>
        }
        rightHeaderClassName={styles.rightHeader}
      >
        <div className={styles.upperSide}>
          <div
            className={styles.description}
            style={{
              backgroundImage: `url(${backgroundGraphics})`,
              backgroundPosition: 'right bottom',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className={styles.descriptionHeader}>
              <Typography variant="title1" sx={{ color: palette.white.main }}>
                Detaljni opis tendera
              </Typography>
            </div>
            <Divider className={styles.divider} flexItem />
            <div className={styles.rteWrapper}>
              {content && !content.includes('<error>') ? (
                parse(content)
              ) : (
                <Typography variant="title5">Nema opisa</Typography>
              )}
            </div>
          </div>
        </div>
        <div className={styles.bottomSide}>
          <div
            className={styles.topic}
            style={{
              backgroundImage: `url(${backgroundGraphics})`,
              backgroundPosition: 'right bottom',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className={styles.textWrapper}>
              <Typography variant="title1" sx={{ color: palette.white.main }}>
                Oblasti rada koje su potrebne
              </Typography>
            </div>
            <Divider className={styles.divider} flexItem />
            <div className={styles.topicTypes}>
              {proposal.tender.topics?.map((topic: Topic) => getServices(topic))}
            </div>
          </div>
          <div className={styles.rightSide}>
            <div className={styles.date}>
              <div className={styles.textWrapper}>
                <Typography
                  variant="title1"
                  sx={{ color: palette.white.main, textAlign: 'center', width: '100%' }}
                >
                  Očekivani datum završetka implementacije
                </Typography>
              </div>
              <Divider className={styles.divider} flexItem />
              <div className={styles.valueWrapper}>
                <Typography
                  variant="title1"
                  sx={{ color: palette.charts.green200.main }}
                  className={styles.infoValue}
                >
                  {formatDate(new Date(proposal.tender.deadline))}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </VendorInfoPanel>
    </div>
  ) : (
    <div className={styles.loader}>
      <CircularProgress
        size={35}
        sx={{ marginLeft: '4px', color: palette.white.main, alignSelf: 'center' }}
      />
    </div>
  )
}

export default TenderDetails
